import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Collapse, Tooltip, message } from 'antd';
import {
  RobotOutlined,
  SendOutlined,
  UserOutlined,
  ToolOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { HomeContainer } from '../Home/styles';
import {
  getAiHealth,
  sendAiChat,
  type AiHealth,
  type AiTraceStep,
} from '../../services/aiAssistantApi';
import * as S from './styles';

interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  trace?: AiTraceStep[];
  steps?: number;
  error?: boolean;
}

const SUGGESTIONS: { icon: React.ReactNode; text: string }[] = [
  {
    icon: <LineChartOutlined />,
    text: 'Is FPT a good fit for a grid strategy? Analyse from 2024-01-01 to 2024-06-01.',
  },
  {
    icon: <ThunderboltOutlined />,
    text: 'Suggest grid parameters for HPG based on the last 3 months of ATR.',
  },
  {
    icon: <SwapOutlined />,
    text: 'Analyse the VIC / VHM pair: spread, z-score, and entry/exit thresholds.',
  },
];

/** Tiny, dependency-free markdown-ish renderer for the assistant's plain-text
 *  answers: paragraphs, `- ` bullet lists, **bold**, and `inline code`. */
const renderRichText = (text: string): React.ReactNode => {
  const inline = (s: string, keyBase: string): React.ReactNode[] =>
    s
      .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
      .filter(Boolean)
      .map((part, i) => {
        const key = `${keyBase}-${i}`;
        if (part.startsWith('**') && part.endsWith('**'))
          return <strong key={key}>{part.slice(2, -2)}</strong>;
        if (part.startsWith('`') && part.endsWith('`'))
          return <code key={key}>{part.slice(1, -1)}</code>;
        return <React.Fragment key={key}>{part}</React.Fragment>;
      });

  const blocks: React.ReactNode[] = [];
  const lines = text.split('\n');
  let bullets: string[] = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    const items = bullets;
    blocks.push(
      <ul key={`ul-${blocks.length}`}>
        {items.map((b, i) => (
          <li key={i}>{inline(b, `li-${blocks.length}-${i}`)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-•]\s+(.*)$/);
    if (bullet) {
      bullets.push(bullet[1]);
      return;
    }
    flushBullets();
    if (line.trim() === '') return;
    blocks.push(<p key={`p-${idx}`}>{inline(line, `p-${idx}`)}</p>);
  });
  flushBullets();

  return blocks.length ? blocks : text;
};

const AiAssistantPage: React.FC = () => {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [health, setHealth] = useState<AiHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAiHealth()
      .then((h) => {
        setHealth(h);
        setHealthErr(null);
      })
      .catch((e) =>
        setHealthErr(e?.response?.data?.message ?? e?.message ?? 'Agent unreachable'),
      );
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, sending]);

  const send = useCallback(
    async (text: string) => {
      const msg = text.trim();
      if (!msg || sending) return;
      setDraft('');
      setTurns((prev) => [...prev, { role: 'user', content: msg }]);
      setSending(true);
      try {
        const res = await sendAiChat(msg);
        setTurns((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: res.answer || '(no answer returned)',
            trace: res.trace,
            steps: res.steps,
          },
        ]);
      } catch (e: any) {
        const detail = e?.response?.data?.message ?? e?.message ?? 'Unknown error';
        setTurns((prev) => [...prev, { role: 'assistant', content: detail, error: true }]);
        message.error('Failed to reach the AI assistant');
      } finally {
        setSending(false);
      }
    },
    [sending],
  );

  const healthState: 'online' | 'offline' | 'loading' = healthErr
    ? 'offline'
    : health
    ? 'online'
    : 'loading';

  const healthLabel = useMemo(() => {
    if (healthErr) return 'Agent offline';
    if (health) return `Agent online · ${health.skills.length} skills`;
    return 'Checking…';
  }, [health, healthErr]);

  return (
    <HomeContainer>
      <S.Page>
        <S.Header>
          <S.TitleRow>
            <S.TitleBadge>
              <RobotOutlined />
            </S.TitleBadge>
            <div>
              <S.Title>AI Assistant</S.Title>
              <S.Subtitle>
                A market-analysis assistant — strategy parameter suggestions, volatility, and
                pair-trading insights. You decide what to buy or sell; the assistant never places
                orders.
              </S.Subtitle>
            </div>
          </S.TitleRow>

          <Tooltip
            title={
              healthErr
                ? healthErr
                : health
                ? `LLM: ${health.llmModel ?? '—'} @ ${health.llmBaseUrl}`
                : ''
            }
          >
            <S.HealthPill $state={healthState}>
              <S.Dot $state={healthState} />
              {healthLabel}
            </S.HealthPill>
          </Tooltip>
        </S.Header>

        <S.ChatShell>
          <S.Messages ref={scrollRef}>
            {turns.length === 0 && (
              <S.Empty>
                <S.EmptyIcon>
                  <RobotOutlined />
                </S.EmptyIcon>
                <div>
                  <S.EmptyTitle>How can I help you trade smarter?</S.EmptyTitle>
                  <S.EmptyHint>
                    Ask in English or Vietnamese. Try one of these to get started:
                  </S.EmptyHint>
                </div>
                <S.SuggestionGrid>
                  {SUGGESTIONS.map((s) => (
                    <S.SuggestionCard
                      key={s.text}
                      onClick={() => send(s.text)}
                      disabled={sending || !!healthErr}
                    >
                      <span className="icon">{s.icon}</span>
                      {s.text}
                    </S.SuggestionCard>
                  ))}
                </S.SuggestionGrid>
              </S.Empty>
            )}

            {turns.map((t, i) => (
              <ChatBubble key={i} turn={t} />
            ))}

            {sending && (
              <S.Row $user={false}>
                <S.Avatar $user={false}>
                  <RobotOutlined />
                </S.Avatar>
                <S.Bubble $user={false}>
                  <S.Typing>
                    <span />
                    <span />
                    <span />
                  </S.Typing>
                </S.Bubble>
              </S.Row>
            )}
          </S.Messages>

          <S.Composer>
            <S.ComposerInner>
              <S.ChatInput
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    send(draft);
                  }
                }}
                placeholder={
                  healthErr ? 'Agent is offline…' : 'Ask anything about the market…'
                }
                autoSize={{ minRows: 1, maxRows: 5 }}
                disabled={sending || !!healthErr}
              />
              <S.SendButton
                onClick={() => send(draft)}
                disabled={sending || !draft.trim() || !!healthErr}
                aria-label="Send"
              >
                <SendOutlined />
              </S.SendButton>
            </S.ComposerInner>
            <S.ComposerHint>
              Press Enter to send · Shift + Enter for a new line
            </S.ComposerHint>
          </S.Composer>
        </S.ChatShell>
      </S.Page>
    </HomeContainer>
  );
};

const ChatBubble: React.FC<{ turn: ChatTurn }> = ({ turn }) => {
  const isUser = turn.role === 'user';
  return (
    <S.Row $user={isUser}>
      <S.Avatar $user={isUser}>{isUser ? <UserOutlined /> : <RobotOutlined />}</S.Avatar>
      <S.Bubble $user={isUser} $error={turn.error}>
        {isUser ? turn.content : renderRichText(turn.content)}
        {!isUser && turn.trace && turn.trace.length > 0 && (
          <Collapse
            ghost
            size="small"
            items={[
              {
                key: 'trace',
                label: (
                  <span style={{ color: '#848E9C', fontSize: 12 }}>
                    <ToolOutlined /> {turn.trace.length} tool call(s) · {turn.steps} step(s)
                  </span>
                ),
                children: <S.TracePre>{JSON.stringify(turn.trace, null, 2)}</S.TracePre>,
              },
            ]}
          />
        )}
      </S.Bubble>
    </S.Row>
  );
};

export default AiAssistantPage;
